/**
 * AP-34 8. — **Rechnen der Eingangsrechnung.**
 *
 * Derselbe Kern wie im Belegwesen (27.2-6): die Ganzzahlarithmetik aus `documents-calc.ts`
 * (`skaliert`, `teileGerundet`). Eigen ist nur, was die Eingangsseite anders hat — der Einzelpreis
 * mit **vier** Nachkommastellen (27.2-8), die Steuer je Steuer**code** und das Vorzeichen an der
 * Zeile (Gutschrift = negative Menge, Abzug einer Teilrechnung = negative Zeile).
 */
import { centZuBetrag, skaliert, teileGerundet } from './documents-calc'
import type { IncomingCostTarget, IncomingLineType } from './incoming-invoices'

export interface EingangRechenZeile {
  key: string
  lineType: IncomingLineType
  /** drei Nachkommastellen, mit Vorzeichen */
  quantity: number
  /** vier Nachkommastellen */
  unitPrice: number
  priceUnit: number
  discountPercent: number | null
  taxCodeKey: string | null
  /** Satz des Steuercodes in Prozent */
  taxRate: number | null
  /** Bei Reverse Charge, innergemeinschaftlichem Erwerb und Kleinunternehmer steht keine Steuer auf der Rechnung. */
  steuerAufRechnung: boolean
}

export interface EingangZeilenErgebnis {
  key: string
  lineNet: number
  lineTax: number
}

export interface EingangSteuerGruppe {
  taxCodeKey: string
  taxRate: number
  netAmount: number
  taxAmount: number
}

export interface EingangRechenErgebnis {
  lines: EingangZeilenErgebnis[]
  taxes: EingangSteuerGruppe[]
  netTotal: number
  taxTotal: number
  grossTotal: number
}

/** `Menge × Einzelpreis ÷ Preiseinheit × (1 − Rabatt%)` in Cent, einmal gerundet. */
export function eingangZeileNettoCent(quantity: number, unitPrice: number, priceUnit: number, discountPercent: number | null): bigint {
  const mengeMilli = skaliert(quantity, 3)
  const preisZehntausendstel = skaliert(unitPrice, 4)
  const rabattBp = skaliert(discountPercent ?? 0, 2)
  const pe = BigInt(priceUnit > 0 ? priceUnit : 1)
  // Tausendstel × Zehntausendstel Euro × Zehntausendstel Faktor → Cent
  return teileGerundet(mengeMilli * preisZehntausendstel * (10000n - rabattBp), pe * 1_000_000_000n)
}

/**
 * Rechnet Zeilen, Steuer je Code und Summen (8.1, 7.3).
 *
 * Die Steuer entsteht **auf der Summe der Gruppe** (Steuercode und Satz), nicht je Zeile — so
 * rechnet auch der Lieferant. Auf die Zeilen wird sie danach verteilt, damit die Projektkosten bei
 * nicht abzugsfähiger Vorsteuer aufgehen (14.1); der Rundungsrest fällt auf die betragsgrößte Zeile.
 */
export function berechneEingangsrechnung(zeilen: readonly EingangRechenZeile[]): EingangRechenErgebnis {
  const netto = new Map<string, bigint>()
  const gruppen = new Map<string, { taxCodeKey: string; taxRate: number; netto: bigint; keys: string[] }>()
  for (const z of zeilen) {
    if (z.lineType === 'TEXT') {
      netto.set(z.key, 0n)
      continue
    }
    const n = eingangZeileNettoCent(z.quantity, z.unitPrice, z.priceUnit, z.discountPercent)
    netto.set(z.key, n)
    if (!z.taxCodeKey) continue
    const satz = z.steuerAufRechnung ? (z.taxRate ?? 0) : 0
    const g = `${z.taxCodeKey}|${satz}`
    const gruppe = gruppen.get(g) ?? { taxCodeKey: z.taxCodeKey, taxRate: satz, netto: 0n, keys: [] }
    gruppe.netto += n
    gruppe.keys.push(z.key)
    gruppen.set(g, gruppe)
  }

  const steuerJeZeile = new Map<string, bigint>()
  const taxes: EingangSteuerGruppe[] = []
  let steuerGesamt = 0n
  for (const g of gruppen.values()) {
    const steuer = teileGerundet(g.netto * skaliert(g.taxRate, 2), 10000n)
    steuerGesamt += steuer
    taxes.push({ taxCodeKey: g.taxCodeKey, taxRate: g.taxRate, netAmount: centZuBetrag(g.netto), taxAmount: centZuBetrag(steuer) })
    // auf die Zeilen der Gruppe verteilen, Rest auf die betragsgrößte
    let verteilt = 0n
    let groesste: string | null = null
    let groessterBetrag = -1n
    for (const key of g.keys) {
      const n = netto.get(key) ?? 0n
      const anteil = g.netto === 0n ? 0n : teileGerundet(steuer * n, g.netto)
      steuerJeZeile.set(key, anteil)
      verteilt += anteil
      const betrag = n < 0n ? -n : n
      if (betrag > groessterBetrag) {
        groessterBetrag = betrag
        groesste = key
      }
    }
    if (groesste && verteilt !== steuer) steuerJeZeile.set(groesste, (steuerJeZeile.get(groesste) ?? 0n) + (steuer - verteilt))
  }

  let nettoGesamt = 0n
  for (const n of netto.values()) nettoGesamt += n
  return {
    lines: zeilen.map((z) => ({ key: z.key, lineNet: centZuBetrag(netto.get(z.key) ?? 0n), lineTax: centZuBetrag(steuerJeZeile.get(z.key) ?? 0n) })),
    taxes,
    netTotal: centZuBetrag(nettoGesamt),
    taxTotal: centZuBetrag(steuerGesamt),
    grossTotal: centZuBetrag(nettoGesamt + steuerGesamt),
  }
}

/**
 * 14.1 / Nachtrag 2 3.4 — **was eine Zeile kostet**: netto; bei nicht abzugsfähiger Vorsteuer netto
 * plus Steuer. Eine Funktion für Projektkosten und Gemeinkosten.
 */
export function kostenBetrag(zeile: { lineNet: number; lineTax: number; inputTaxDeductible: boolean }): number {
  const cent = skaliert(zeile.lineNet, 2) + (zeile.inputTaxDeductible ? 0n : skaliert(zeile.lineTax, 2))
  return centZuBetrag(cent)
}

/** Verfahren, bei denen die Steuer auf der Rechnung steht — bei allen anderen schuldet sie der Empfänger oder sie fällt nicht an. */
export function steuerStehtAufRechnung(procedure: string): boolean {
  return procedure === 'DOMESTIC' || procedure === 'NO_INPUT_TAX' || procedure === 'IMPORT'
}

/**
 * Nachtrag 2, 2.1 — die drei Kostenziele schließen einander aus. Liefert die Felder, die beim
 * Wechsel des Ziels zu leeren sind (Folgefelder im selben PATCH).
 */
export function kostenzielFolge(ziel: IncomingCostTarget | null): Record<string, null> {
  const projekt = { projectId: null, projectNumber: null, projectName: null, projectPositionId: null, projectPositionNumber: null, projectPositionName: null }
  const kategorie = { overheadCategoryId: null, overheadCategoryName: null }
  if (ziel === 'PROJECT') return kategorie
  if (ziel === 'OVERHEAD') return projekt
  return { ...projekt, ...kategorie }
}

/** Das Kostenziel im Kopf einer Eingangsrechnung — die Vorgabe für ihre Positionen. */
export interface KopfKostenziel {
  defaultCostTarget: IncomingCostTarget | null
  defaultProjectId: string | null
  defaultOverheadCategoryId: string | null
}

/** Was eine Position davon trägt. */
export interface ZeilenKostenziel {
  lineType: string
  costTarget: IncomingCostTarget | null
  projectId: string | null
  overheadCategoryId: string | null
}

/** Ein Ziel ohne seine Zuordnung zählt als fehlend (Nachtrag 2, 2.1): Projekt ohne Projekt, Gemeinkosten ohne Kategorie. */
const zielSchluessel = (ziel: IncomingCostTarget | null, projectId: string | null, overheadCategoryId: string | null): string | null => {
  if (ziel === 'PROJECT') return projectId ? `PROJECT|${projectId}` : null
  if (ziel === 'OVERHEAD') return overheadCategoryId ? `OVERHEAD|${overheadCategoryId}` : null
  return ziel === 'STOCK' ? 'STOCK' : null
}

/** Trägt der Kopf ein vollständiges Kostenziel — eines, das sich in Positionen eintragen lässt? */
export const kopfKostenzielVollstaendig = (kopf: KopfKostenziel): boolean => zielSchluessel(kopf.defaultCostTarget, kopf.defaultProjectId, kopf.defaultOverheadCategoryId) !== null

/**
 * AP-34 Nachtrag 6 (PT-23) — **wie eine Position zum Kostenziel des Kopfs steht.** Eine Regel für die Maske (sie
 * zählt, was einzutragen wäre) und den Server (er trägt ein).
 *
 * - `MISSING` — die Position hat kein Kostenziel (oder eines ohne Projekt bzw. Kategorie),
 * - `OTHER` — sie trägt ein anderes,
 * - `SAME` — sie trägt schon das des Kopfs; ihre Projektposition zählt dabei nicht, die gehört der Position,
 * - `null` — nicht betroffen: Textzeilen haben kein Kostenziel, und der Abzug einer Schlussrechnung trägt das der
 *   abgezogenen Teilrechnung. Ebenso alles, solange der Kopf kein vollständiges Kostenziel hat.
 */
export function kostenzielZumKopf(zeile: ZeilenKostenziel, kopf: KopfKostenziel): 'MISSING' | 'OTHER' | 'SAME' | null {
  if (zeile.lineType !== 'ITEM' && zeile.lineType !== 'CHARGE') return null
  const soll = zielSchluessel(kopf.defaultCostTarget, kopf.defaultProjectId, kopf.defaultOverheadCategoryId)
  if (!soll) return null
  const ist = zielSchluessel(zeile.costTarget, zeile.projectId, zeile.overheadCategoryId)
  return !ist ? 'MISSING' : ist === soll ? 'SAME' : 'OTHER'
}
